import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

interface VerificationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  verificationDetails: any;
  configName: string;
  configId: number;
  providerType: string;
}

const VerificationDetailsModal: React.FC<VerificationDetailsModalProps> = ({
  isOpen,
  onClose,
  verificationDetails,
  configName,
  configId,
  providerType,
}) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || !verificationDetails) return null;

  const isVerified = verificationDetails.verification_status === "verified";
  const hasError = !!verificationDetails.error;
  const hasPhoneDetails = !!verificationDetails.phone_details;
  const hasAvailablePhones = verificationDetails.available_phones?.length > 0;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9000,
        padding: 24,
        boxSizing: "border-box",
      }}>
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 800,
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow:
            "0 24px 48px rgba(15,23,42,0.35), 0 0 0 1px rgba(148,163,184,0.18)",
          display: "flex",
          flexDirection: "column",
        }}>
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
          style={{
            background: isVerified ? "#f0fdf4" : "#fef2f2",
          }}>
          <div>
            <h5 className="mb-0 fw-semibold d-flex align-items-center">
              {isVerified ? (
                <CheckCircleOutlined className="text-success me-2 fs-20" />
              ) : (
                <CloseCircleOutlined className="text-danger me-2 fs-20" />
              )}
              Credential Verification Details
            </h5>
            <small className="text-muted">
              {configName} (ID: {configId})
            </small>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              color: "#6b7280",
            }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "18px 24px",
            overflowY: "auto",
            flex: 1,
          }}>
          {/* Status Banner */}
          <div
            className={`alert ${
              isVerified ? "alert-success" : "alert-danger"
            } mb-3`}>
            <div className="d-flex align-items-center mb-2">
              {isVerified ? (
                <CheckCircleOutlined className="fs-20 me-2" />
              ) : (
                <CloseCircleOutlined className="fs-20 me-2" />
              )}
              <strong className="fs-16">
                {isVerified ? "Verification Successful" : "Verification Failed"}
              </strong>
            </div>
            {verificationDetails.verified_at && (
              <small className="text-muted">
                Last checked:{" "}
                {new Date(verificationDetails.verified_at).toLocaleString()}
              </small>
            )}
          </div>

          {/* Success - Provider Details */}
          {isVerified && (
            <div>
              {/* WhatsApp Direct */}
              {providerType === "whatsapp_direct" && (
                <div className="row g-3">
                  {verificationDetails.verified_name && (
                    <div className="col-6">
                      <div className="text-muted small">Verified Name</div>
                      <div className="fw-semibold">
                        {verificationDetails.verified_name}
                      </div>
                    </div>
                  )}
                  {verificationDetails.display_phone_number && (
                    <div className="col-6">
                      <div className="text-muted small">Phone Number</div>
                      <div className="fw-semibold">
                        {verificationDetails.display_phone_number}
                      </div>
                    </div>
                  )}
                  {verificationDetails.phone_number_id && (
                    <div className="col-6">
                      <div className="text-muted small">Phone Number ID</div>
                      <div className="fw-semibold font-monospace small">
                        {verificationDetails.phone_number_id}
                      </div>
                    </div>
                  )}
                  {verificationDetails.platform_type && (
                    <div className="col-6">
                      <div className="text-muted small">Platform Type</div>
                      <div>
                        <Tag
                          color={
                            verificationDetails.platform_type === "CLOUD_API"
                              ? "blue"
                              : "default"
                          }>
                          {verificationDetails.platform_type}
                        </Tag>
                      </div>
                    </div>
                  )}
                  {verificationDetails.quality_rating && (
                    <div className="col-6">
                      <div className="text-muted small">Quality Rating</div>
                      <div>
                        <Tag
                          color={
                            verificationDetails.quality_rating === "GREEN"
                              ? "success"
                              : verificationDetails.quality_rating === "YELLOW"
                              ? "warning"
                              : "error"
                          }>
                          {verificationDetails.quality_rating}
                        </Tag>
                      </div>
                    </div>
                  )}
                  {verificationDetails.code_verification_status && (
                    <div className="col-6">
                      <div className="text-muted small">Code Verification</div>
                      <div>
                        <Tag
                          color={
                            verificationDetails.code_verification_status ===
                            "VERIFIED"
                              ? "success"
                              : "warning"
                          }>
                          {verificationDetails.code_verification_status}
                        </Tag>
                      </div>
                    </div>
                  )}
                  {verificationDetails.throughput_level && (
                    <div className="col-6">
                      <div className="text-muted small">Throughput Level</div>
                      <div className="fw-semibold">
                        {verificationDetails.throughput_level}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Twilio */}
              {providerType === "twilio" && (
                <div className="row g-3">
                  {verificationDetails.account_name && (
                    <div className="col-6">
                      <div className="text-muted small">Account Name</div>
                      <div className="fw-semibold">
                        {verificationDetails.account_name}
                      </div>
                    </div>
                  )}
                  {verificationDetails.account_sid && (
                    <div className="col-6">
                      <div className="text-muted small">Account SID</div>
                      <div className="fw-semibold font-monospace small">
                        {verificationDetails.account_sid}
                      </div>
                    </div>
                  )}
                  {verificationDetails.account_type && (
                    <div className="col-6">
                      <div className="text-muted small">Account Type</div>
                      <div>
                        <Tag
                          color={
                            verificationDetails.account_type === "Trial"
                              ? "warning"
                              : "success"
                          }>
                          {verificationDetails.account_type}
                        </Tag>
                      </div>
                    </div>
                  )}
                  {verificationDetails.account_status && (
                    <div className="col-6">
                      <div className="text-muted small">Account Status</div>
                      <div>
                        <Tag
                          color={
                            verificationDetails.account_status === "active"
                              ? "success"
                              : "error"
                          }>
                          {verificationDetails.account_status.toUpperCase()}
                        </Tag>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fonada / Kaleyra */}
              {(providerType === "fonada" || providerType === "kaleyra") && (
                <div className="row g-3">
                  {verificationDetails.api_key_masked && (
                    <div className="col-6">
                      <div className="text-muted small">API Key</div>
                      <div className="fw-semibold font-monospace small">
                        {verificationDetails.api_key_masked}
                      </div>
                    </div>
                  )}
                  {verificationDetails.from_number && (
                    <div className="col-6">
                      <div className="text-muted small">From Number</div>
                      <div className="fw-semibold">
                        {verificationDetails.from_number}
                      </div>
                    </div>
                  )}
                  {verificationDetails.api_url && (
                    <div className="col-12">
                      <div className="text-muted small">API URL</div>
                      <div className="fw-semibold small">
                        {verificationDetails.api_url}
                      </div>
                    </div>
                  )}
                  {verificationDetails.note && (
                    <div className="col-12">
                      <div className="alert alert-info mb-0 py-2">
                        <small>
                          <i className="ti ti-info-circle me-1" />
                          {verificationDetails.note}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error Details */}
          {hasError && (
            <div className="mb-3">
              <strong>Error Message:</strong>
              <div className="mt-2 p-3 bg-white rounded border border-danger">
                {verificationDetails.error}
              </div>
            </div>
          )}

          {/* Suggestion */}
          {verificationDetails.suggestion && (
            <div className="alert alert-warning mt-3">
              <strong>
                <i className="ti ti-bulb me-1" />
                Suggestion:
              </strong>
              <div className="mt-1">{verificationDetails.suggestion}</div>
            </div>
          )}

          {/* Phone Details (when failed) */}
          {hasPhoneDetails && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <WarningOutlined className="text-warning me-2" />
                <strong>Current Phone Number Details:</strong>
              </div>
              <div className="card border">
                <div className="card-body p-3">
                  <div className="row g-2">
                    <div className="col-6">
                      <small className="text-muted">Display Number</small>
                      <div className="fw-semibold">
                        {verificationDetails.phone_details.display_phone_number}
                      </div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Phone Number ID</small>
                      <div className="small font-monospace">
                        {verificationDetails.phone_details.phone_number_id}
                      </div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Platform Type</small>
                      <div>
                        <Tag
                          color={
                            verificationDetails.phone_details.platform_type ===
                            "CLOUD_API"
                              ? "blue"
                              : "default"
                          }>
                          {verificationDetails.phone_details.platform_type}
                        </Tag>
                      </div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Verification Status</small>
                      <div>
                        <Tag
                          color={
                            verificationDetails.phone_details
                              .code_verification_status === "VERIFIED"
                              ? "success"
                              : verificationDetails.phone_details
                                  .code_verification_status === "EXPIRED"
                              ? "error"
                              : "warning"
                          }>
                          {
                            verificationDetails.phone_details
                              .code_verification_status
                          }
                        </Tag>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Available Phones */}
          {hasAvailablePhones && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-3">
                <WarningOutlined className="text-warning me-2" />
                <strong>Available Phone Numbers in WABA:</strong>
              </div>

              <div className="row g-3">
                {verificationDetails.available_phones.map(
                  (phone: any, index: number) => (
                    <div
                      key={phone.phone_number_id || index}
                      className="col-12">
                      <div className="card border">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="mb-0">
                              <i className="ti ti-phone me-2 text-info" />
                              {phone.display_phone_number}
                            </h6>
                            <Tag
                              color={
                                phone.code_verification_status === "VERIFIED"
                                  ? "success"
                                  : phone.code_verification_status === "EXPIRED"
                                  ? "error"
                                  : "warning"
                              }>
                              {phone.code_verification_status}
                            </Tag>
                          </div>

                          <div className="row g-2 mt-2">
                            <div className="col-6">
                              <small className="text-muted">
                                Phone Number ID
                              </small>
                              <div className="small fw-semibold font-monospace">
                                {phone.phone_number_id}
                              </div>
                            </div>
                            <div className="col-6">
                              <small className="text-muted">
                                Platform Type
                              </small>
                              <div>
                                <Tag
                                  color={
                                    phone.platform_type === "CLOUD_API"
                                      ? "blue"
                                      : "default"
                                  }>
                                  {phone.platform_type}
                                </Tag>
                              </div>
                            </div>
                            <div className="col-6">
                              <small className="text-muted">
                                Quality Rating
                              </small>
                              <div>
                                <Tag
                                  color={
                                    phone.quality_rating === "GREEN"
                                      ? "success"
                                      : phone.quality_rating === "YELLOW"
                                      ? "warning"
                                      : phone.quality_rating === "UNKNOWN"
                                      ? "default"
                                      : "error"
                                  }>
                                  {phone.quality_rating || "UNKNOWN"}
                                </Tag>
                              </div>
                            </div>
                            <div className="col-6">
                              <small className="text-muted">Throughput</small>
                              <div className="small fw-semibold">
                                {phone.throughput_level || "N/A"}
                              </div>
                            </div>
                          </div>

                          {phone.code_verification_status === "EXPIRED" && (
                            <div className="alert alert-warning mt-2 mb-0 py-2">
                              <small>
                                <WarningOutlined className="me-1" />
                                This number needs re-verification in Meta
                                Business Manager
                              </small>
                            </div>
                          )}

                          {phone.platform_type !== "CLOUD_API" && (
                            <div className="alert alert-info mt-2 mb-0 py-2">
                              <small>
                                <i className="ti ti-info-circle me-1" />
                                Not compatible with Cloud API. Migration
                                required.
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="alert alert-info mt-3 mb-0">
                <strong>💡 Recommendation:</strong>
                <ul className="mb-0 mt-2 ps-3">
                  <li>
                    Update your configuration to use one of the available phone
                    number IDs above
                  </li>
                  <li>
                    Ensure the phone number is <strong>VERIFIED</strong>
                  </li>
                  <li>
                    Use only <strong>CLOUD_API</strong> platform type numbers
                  </li>
                  <li>Re-verify EXPIRED numbers in Meta Business Manager</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-end px-4 py-3 border-top">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VerificationDetailsModal;
